import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, X, Users, Wifi, Gamepad2 } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { LanguageSelector } from '../components/LanguageSelector';

interface CreateRoomPageProps {
  onBack: () => void;
  onCreateRoom: (playerName: string, isLocal: boolean) => void;
  onCreateLocalRoom: (players: string[]) => void;
}

export const CreateRoomPage: React.FC<CreateRoomPageProps> = ({
  onBack,
  onCreateRoom,
  onCreateLocalRoom,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'online' | 'local'>('online');
  const [playerName, setPlayerName] = useState('');
  const [localPlayers, setLocalPlayers] = useState(['']);
  const [loading, setLoading] = useState(false);

  const addPlayer = () => {
    setLocalPlayers([...localPlayers, '']);
  };

  const removePlayer = (index: number) => {
    if (localPlayers.length > 1) {
      setLocalPlayers(localPlayers.filter((_, i) => i !== index));
    }
  };

  const updatePlayer = (index: number, name: string) => {
    const newPlayers = [...localPlayers];
    newPlayers[index] = name;
    setLocalPlayers(newPlayers);
  };

  const handleCreateOnlineRoom = async () => {
    if (!playerName.trim()) return;
    setLoading(true);
    await onCreateRoom(playerName.trim(), false);
    setLoading(false);
  };

  const handleCreateLocalRoom = async () => {
    const validPlayers = localPlayers.filter(name => name.trim());
    console.log('Valid players:', validPlayers);
    console.log('Valid players length:', validPlayers.length);
    
    if (validPlayers.length < 3) {
      console.log('Not enough players, returning');
      alert('Se necesitan al menos 3 jugadores para crear la sala');
      return;
    }
    
    console.log('Creating local room with players:', validPlayers);
    setLoading(true);
    try {
      await onCreateLocalRoom(validPlayers);
      console.log('Local room created successfully');
    } catch (error) {
      console.error('Error in handleCreateLocalRoom:', error);
    } finally {
      setLoading(false);
    }
  };

  const canCreateLocal = localPlayers.filter(name => name.trim()).length >= 3;
  const canCreateOnline = playerName.trim().length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('createRoom.title')}
          </h1>
        </div>

        {/* Mode Selection */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('online')}
              className={`p-4 rounded-xl transition-all duration-200 ${
                mode === 'online'
                  ? 'bg-primary-500/20 border-2 border-primary-400'
                  : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Wifi className="w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  {t('createRoom.onlineMode')}
                </h3>
              </div>
              <p className="text-white/70 text-sm text-left">
                {t('createRoom.onlineModeDescription')}
              </p>
            </button>

            <button
              onClick={() => setMode('local')}
              className={`p-4 rounded-xl transition-all duration-200 ${
                mode === 'local'
                  ? 'bg-accent-500/20 border-2 border-accent-400'
                  : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Gamepad2 className="w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  {t('createRoom.localMode')}
                </h3>
              </div>
              <p className="text-white/70 text-sm text-left">
                {t('createRoom.localModeDescription')}
              </p>
            </button>
          </div>
        </Card>

        {/* Online Mode */}
        {mode === 'online' && (
          <Card>
            <div className="mb-6">
              <Input
                label={t('createRoom.playerName')}
                placeholder={t('createRoom.playerNamePlaceholder')}
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                icon={<Users className="w-5 h-5" />}
              />
            </div>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleCreateOnlineRoom}
              disabled={!canCreateOnline || loading}
              loading={loading}
              icon={<Wifi className="w-5 h-5" />}
            >
              {t('createRoom.createOnlineRoom')}
            </Button>
          </Card>
        )}

        {/* Local Mode */}
        {mode === 'local' && (
          <Card>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t('createRoom.addPlayers')}
              </h3>
              <div className="space-y-3">
                {localPlayers.map((player, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`${t('createRoom.playerNameInput')} ${index + 1}`}
                      value={player}
                      onChange={(e) => updatePlayer(index, e.target.value)}
                      icon={<Users className="w-5 h-5" />}
                    />
                    {localPlayers.length > 1 && (
                      <Button
                        variant="danger"
                        size="md"
                        onClick={() => removePlayer(index)}
                        icon={<X className="w-4 h-4" />}
                      >
                        {t('createRoom.removePlayer')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={addPlayer}
                icon={<Plus className="w-4 h-4" />}
                className="mt-3"
              >
                {t('createRoom.addPlayer')}
              </Button>
            </div>
            <Button
              variant="accent"
              size="lg"
              fullWidth
              onClick={handleCreateLocalRoom}
              disabled={!canCreateLocal || loading}
              loading={loading}
              icon={<Gamepad2 className="w-5 h-5" />}
            >
              {t('createRoom.startGame')}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};
